import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useTeamMember, useTeamMemberStats } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { RepAvatar } from "@/components/v2/rep-avatar";
import { StatusBadge } from "@/components/status-badge";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  sales_rep: "Sales Rep",
  production: "Production",
  finance: "Finance",
};

export default function V2TeamMemberDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useTeamMember(id ?? "");
  const teamMember = (data as { team_member?: Row } | undefined)?.team_member;
  // URL :id is the contact_id, but stats and the activity feed key off the
  // team-member's contact_role_id. Wait for the row to resolve, then thread
  // the role id through — falls back to "" so the hooks stay disabled until
  // load completes.
  const contactRoleId = str(teamMember?.contact_role_id) ?? "";
  const { data: statsData } = useTeamMemberStats(contactRoleId);
  const stats = statsData as
    | { total_proposals?: number; total_jobs?: number; total_revenue?: number }
    | undefined;

  const firstName = str(teamMember?.first_name) ?? "";
  const lastName = str(teamMember?.last_name) ?? "";
  const displayName =
    [firstName, lastName].filter(Boolean).join(" ") || "Team Member";
  const role = str(teamMember?.role);
  const isActive = teamMember?.is_active === true;
  const displayTitle = str(teamMember?.display_title);
  const email = str(teamMember?.email);
  const phone = str(teamMember?.phone);
  const territoryId = str(teamMember?.territory_id);
  const commissionTier = str(teamMember?.commission_tier);
  const headshotUrl = str(teamMember?.headshot_url);

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !teamMember}
      title={displayName}
      subtitle={displayTitle ?? undefined}
      breadcrumbs={[
        { label: "Team Members", href: "/team-members" },
        { label: displayName },
      ]}
      actions={<DetailPageActions entityLabel="Team Member" />}
      notFoundMessage={{
        title: "Team member not found",
        description: "This team member may have been deleted or deactivated.",
      }}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-5">
          {/* Identity hero (§A6.1): 96px face-cropped headshot beside the
              inline properties strip. §A4.1 — team members always prefer the
              real photo; RepAvatar falls back to a monogram when none exists. */}
          <div className="flex items-start gap-4">
            <RepAvatar name={displayName} headshotUrl={headshotUrl} size="xl" />
            {/* Inline properties strip */}
            <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)] pt-1">
              {role && <StatusBadge status={role} />}
              <span className="text-[color:var(--color-text-lo)]">·</span>
              <span>{isActive ? "Active" : "Inactive"}</span>
              {displayTitle && (
                <>
                  <span className="text-[color:var(--color-text-lo)]">·</span>
                  <span>{displayTitle}</span>
                </>
              )}
            </div>
          </div>

          {/* KPI row */}
          <KPIRow
            cards={[
              {
                label: "Role",
                value: role ? (ROLE_LABELS[role] ?? role) : "—",
              },
              { label: "Status", value: isActive ? "Active" : "Inactive" },
              {
                label: "Jobs Completed",
                value:
                  typeof stats?.total_jobs === "number"
                    ? String(stats.total_jobs)
                    : "—",
              },
              {
                label: "Revenue Booked",
                value:
                  typeof stats?.total_revenue === "number"
                    ? formatCurrency(stats.total_revenue)
                    : "—",
              },
            ]}
          />

          {/* Details */}
          <RailSection title="Details">
            <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-2">
              <InfoRow label="Email" value={email} />
              <InfoRow label="Phone" value={phone} />
              <InfoRow label="Display Title" value={displayTitle} />
              <InfoRow
                label="Role"
                value={role ? (ROLE_LABELS[role] ?? role) : null}
              />
              <InfoRow label="Commission Tier" value={commissionTier} />
              <InfoRow label="Territory" value={territoryId} />
            </div>
          </RailSection>

          {/* Activity timeline */}
          {contactRoleId && (
            <EntityActivityFeed
              entityType="team_member"
              entityId={contactRoleId}
            />
          )}
        </div>

        {/* Right rail */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Properties">
            <InfoRow label="Status" value={isActive ? "Active" : "Inactive"} />
            <InfoRow
              label="Role"
              value={role ? (ROLE_LABELS[role] ?? role) : null}
            />
            <InfoRow label="Territory" value={territoryId} />
            <InfoRow
              label="Hire Date"
              value={
                teamMember?.created_at
                  ? formatDate(teamMember.created_at as string)
                  : null
              }
            />
            <InfoRow
              label="Updated"
              value={
                teamMember?.updated_at
                  ? formatDate(teamMember.updated_at as string)
                  : null
              }
            />
          </RailSection>
          {contactRoleId && (
            <RailSection title="Activity">
              <EntityActivityPreview
                entityType="team_member"
                entityId={contactRoleId}
                rowCount={4}
              />
            </RailSection>
          )}
        </div>
      </div>
    </DetailPage>
  );
}
